import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { CAP_FLOOR_CENTS } from "./autopilot-billing";

export type RecipientDateRow = {
  id: string;
  recipient_id: string;
  label: string;
  date_type: string;
  event_date: string;
  recurring: boolean;
  auto_ship: boolean;
  category: string;
  budget_cents: number | null;
  created_at: string;
};

export const DATE_TYPES = [
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
  { value: "work_anniversary", label: "Work anniversary" },
  { value: "graduation", label: "Graduation" },
  { value: "memorial", label: "Memorial" },
  { value: "holiday", label: "Holiday" },
  { value: "milestone", label: "Milestone" },
  { value: "just_because", label: "Just because" },
  { value: "custom", label: "Custom" },
] as const;

/** Types that repeat every year unless the member says otherwise. */
export const RECURRING_BY_DEFAULT = new Set([
  "birthday",
  "anniversary",
  "work_anniversary",
  "holiday",
]);

export const defaultRecurringFor = (dateType: string) => RECURRING_BY_DEFAULT.has(dateType);


export const DATE_CATEGORIES = [
  { value: "florals", label: "Florals" },
  { value: "gifts", label: "Toys & luxury gifts" },
  { value: "egift", label: "Instant e-gift" },
] as const;

const TYPES = new Set(DATE_TYPES.map((t) => t.value as string));
const CATS = new Set(DATE_CATEGORIES.map((c) => c.value as string));

const SELECT_COLS =
  "id, recipient_id, label, date_type, event_date, recurring, auto_ship, category, budget_cents, created_at";

/**
 * Legacy mirror rule: recipients.birthday / recipients.anniversary always
 * reflect the OLDEST-created milestone row of that type, or NULL when none
 * remain. recipient_dates stays the source of truth.
 */
async function syncLegacyDates(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: any,
  userId: string,
  recipientId: string,
) {
  const patch: Record<string, string | null> = {};
  for (const [type, column] of [
    ["birthday", "birthday"],
    ["anniversary", "anniversary"],
  ] as const) {
    const { data } = await supabase
      .from("recipient_dates")
      .select("event_date")
      .eq("user_id", userId)
      .eq("recipient_id", recipientId)
      .eq("date_type", type)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    patch[column] = (data?.event_date as string | undefined) ?? null;
  }
  await supabase.from("recipients").update(patch).eq("id", recipientId).eq("user_id", userId);
}


export type UpsertRecipientDateInput = {
  id?: string;
  recipientId: string;
  label: string;
  dateType: string;
  eventDate: string;
  recurring?: boolean;
  autoShip?: boolean;
  category: string;
  /** Optional ceiling for extra dates. null means "use the catalog standard". */
  budgetCents?: number | null;
};

export const listRecipientDates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<RecipientDateRow[]> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("recipient_dates")
      .select(SELECT_COLS)
      .eq("user_id", userId)
      .order("event_date", { ascending: true });
    if (error) throw new Error("Failed to load milestone dates");
    return (data ?? []) as RecipientDateRow[];
  });

export const upsertRecipientDate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: UpsertRecipientDateInput) => {
    if (!data || typeof data !== "object") throw new Error("Invalid payload");
    if (!data.recipientId) throw new Error("Recipient required");
    if (!data.label?.trim()) throw new Error("Label required");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data.eventDate ?? "")) throw new Error("Valid date required");
    return {
      ...data,
      label: data.label.trim().slice(0, 80),
      dateType: TYPES.has(data.dateType) ? data.dateType : "birthday",
      category: CATS.has(data.category) ? data.category : "florals",
      // A cap is optional. When set it can never fall below the catalog floor,
      // so it can never be mistaken for the price of a gift.
      budgetCents:
        data.budgetCents && data.budgetCents > 0
          ? Math.min(Math.max(Math.round(data.budgetCents), CAP_FLOOR_CENTS), 500000)
          : null,
    };
  })
  .handler(async ({ data, context }): Promise<RecipientDateRow> => {
    const { supabase, userId } = context;

    const { data: owns } = await supabase
      .from("recipients")
      .select("id")
      .eq("id", data.recipientId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!owns) throw new Error("Recipient not found");

    const payload = {
      user_id: userId,
      recipient_id: data.recipientId,
      label: data.label,
      date_type: data.dateType,
      event_date: data.eventDate,
      recurring: data.recurring ?? defaultRecurringFor(data.dateType),
      auto_ship: data.autoShip ?? false,
      category: data.category,
      budget_cents: data.budgetCents,
    };

    const query = data.id
      ? supabase.from("recipient_dates").update(payload).eq("id", data.id).eq("user_id", userId)
      : supabase.from("recipient_dates").insert(payload);

    const { data: row, error } = await query.select(SELECT_COLS).single();
    if (error || !row) throw new Error("Failed to save milestone date");
    await syncLegacyDates(supabase, userId, data.recipientId);
    return row as RecipientDateRow;
  });

export const deleteRecipientDate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => {
    if (!data?.id) throw new Error("Invalid id");
    return data;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: existing } = await supabase
      .from("recipient_dates")
      .select("recipient_id")
      .eq("id", data.id)
      .eq("user_id", userId)
      .maybeSingle();
    const { error } = await supabase
      .from("recipient_dates")
      .delete()
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Error("Failed to delete milestone date");
    if (existing?.recipient_id) {
      await syncLegacyDates(supabase, userId, existing.recipient_id as string);
    }
    return { ok: true };
  });

